import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
  Paper,
  Button,
  Avatar,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MessageIcon from '@mui/icons-material/Message';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import BarChartIcon from '@mui/icons-material/BarChart';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { getSchemeIcon, getServiceLabel, getBookLabel } from '@/lib/schemeIcons';
import ThemeModeToggle from '@/components/ThemeModeToggle';
import UserMenu from '@/components/UserMenu';
import NotificationBell from '@/components/NotificationBell';
import SuperadminSwitcher from '@/components/SuperadminSwitcher';

const initials = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

const HeaderControls = () => (
  <>
    <SuperadminSwitcher />
    <NotificationBell />
    <ThemeModeToggle />
    <UserMenu />
  </>
);

const DRAWER_WIDTH = 256;

const buildAdminLinks = (scheme) => [
  { to: '/dashboard', icon: DashboardIcon, label: 'Dashboard' },
  { to: '/members', icon: PeopleIcon, label: 'Members' },
  { to: '/leads', icon: PersonAddIcon, label: 'Leads' },
  { to: '/calendar', icon: CalendarMonthIcon, label: 'Calendar' },
  { to: '/services', icon: getSchemeIcon(scheme), label: getServiceLabel(scheme) },
  // Renamed from "Customer Communication": 3× longer than sibling labels and
  // it wrapped/truncated at drawer width. "Messages" matches the page content.
  { to: '/messages', icon: MessageIcon, label: 'Messages' },
  { to: '/billing', icon: CreditCardIcon, label: 'Billing' },
  { to: '/staff', icon: AdminPanelSettingsIcon, label: 'Staff' },
  { to: '/reports', icon: BarChartIcon, label: 'Reports' },
  { to: '/settings', icon: SettingsIcon, label: 'Settings' },
];

const trainerLinks = [
  { to: '/dashboard', icon: DashboardIcon, label: 'Dashboard' },
  { to: '/members', icon: PeopleIcon, label: 'My Clients' },
  { to: '/calendar', icon: CalendarMonthIcon, label: 'Schedule' },
  { to: '/messages', icon: MessageIcon, label: 'Messages' },
];

const buildMemberLinks = (scheme) => [
  { to: '/schedule', icon: CalendarMonthIcon, label: 'My Schedule' },
  { to: '/book', icon: getSchemeIcon(scheme), label: getBookLabel(scheme) },
  { to: '/messages', icon: MessageIcon, label: 'Messages' },
  { to: '/billing', icon: CreditCardIcon, label: 'Billing' },
  { to: '/profile', icon: SettingsIcon, label: 'Profile' },
];

const SidebarContent = ({ onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isTrainer, isMember } = useAuth();
  const { branding, mode } = useBranding();
  const logoUrl = mode === 'dark' ? branding.logo_dark_url || branding.logo_url : branding.logo_url;
  const scheme = branding?.scheme;

  const links = isMember
    ? buildMemberLinks(scheme)
    : isTrainer
    ? trainerLinks
    : buildAdminLinks(scheme);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={`${branding.app_name} logo`}
            sx={{ maxHeight: 36, maxWidth: '100%', objectFit: 'contain', display: 'block', mb: 0.5 }}
          />
        ) : (
          <Typography variant="h5" color="primary" sx={{ fontWeight: 'inherit' }}>
            {branding.app_name}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {user?.role?.replace('_', ' ')}
        </Typography>
      </Box>

      <List sx={{ flex: 1, py: 2, px: 1.5 }} data-testid="sidebar-nav">
        {links.map((link) => {
          const active = location.pathname === link.to;
          const Icon = link.icon;
          return (
            <ListItemButton
              key={link.to}
              component={Link}
              to={link.to}
              selected={active}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={onNavigate}
              sx={{
                // Pill selection instead of the previous 3px left border:
                // the border nudged the selected label 3px right and mixed a
                // third shape into the pill-button / 12px-card vocabulary.
                borderRadius: 999,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                  '&:hover': {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.18),
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : 'text.secondary' }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: active ? 600 : 500,
                  color: active ? 'primary.main' : 'text.primary',
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={user?.avatar_url || undefined}
              sx={{ width: 36, height: 36, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 14 }}
            >
              {initials(user?.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {user?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {user?.email}
              </Typography>
            </Box>
          </Stack>
        </Paper>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          data-testid="logout-button"
          sx={{ fontWeight: 700 }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );
};

/**
 * MainLayout
 * New optional props (backwards compatible — every existing call still works):
 *   title — page title shown in the desktop header bar (left side), so pages
 *           no longer need a 48px in-content heading.
 *   badge — small count chip rendered next to the title (e.g. member count).
 */
const MainLayout = ({ children, title, badge }) => {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const drawer = (
    <SidebarContent onNavigate={() => !isLg && setMobileOpen(false)} />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          display: { lg: 'none' },
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          color: 'text.primary',
        }}
        elevation={0}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(true)}
            data-testid="menu-toggle"
            sx={{ color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>
          {title && (
            <Typography variant="subtitle1" fontWeight={700} sx={{ ml: 1 }} noWrap>
              {title}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            <HeaderControls />
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { lg: DRAWER_WIDTH }, flexShrink: { lg: 0 } }} data-testid="sidebar">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, bgcolor: 'background.paper' },
          }}
        >
          <Toolbar sx={{ display: 'flex', justifyContent: 'flex-end', lg: 'none' }}>
            <IconButton onClick={() => setMobileOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, bgcolor: 'background.paper', borderRight: 1, borderColor: 'divider' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // minWidth 0 stops wide page content (tables, chip rows) from
          // inflating the flex item past the viewport on small screens —
          // inner overflowX:auto regions scroll instead of the whole page.
          minWidth: 0,
          width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar sx={{ display: { lg: 'none' } }} />
        <Box
          component="header"
          sx={{
            display: { xs: 'none', lg: 'flex' },
            height: 64,
            alignItems: 'center',
            gap: 2,
            px: 4,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {title && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap sx={{ letterSpacing: '-0.01em' }}>
                {title}
              </Typography>
              {badge != null && (
                <Chip label={badge} size="small" sx={{ borderRadius: 999, fontWeight: 500 }} />
              )}
            </Stack>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            {new Date().toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })}
          </Typography>
          <HeaderControls />
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, lg: 4 } }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
